import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const TabsContext = createContext(null);

// Global ref to allow clearing tabs from outside the context
let globalCloseAllTabs = null;

export const clearAllTabsOnLogout = () => {
  if (globalCloseAllTabs) {
    globalCloseAllTabs();
  }
};

export const TAB_TYPES = {
  // Action tabs
  CHECKOUT: 'checkout',
  CHECKIN: 'checkin',
  NEW_BOOKING: 'new-booking',
  EDIT_BOOKING: 'edit-booking',
  INVOICE_VIEW: 'invoice-view',
  GUEST_DETAILS: 'guest-details',
  GUEST_EDIT: 'guest-edit',
  GUEST_ADD: 'guest-add',
  BOOKING_DETAILS: 'booking-details',
  ROOM_ADD: 'room-add',
  ROOM_EDIT: 'room-edit',
  // Page tabs - each sidebar link
  PAGE_DASHBOARD: 'page-dashboard',
  PAGE_BOOKINGS: 'page-bookings',
  PAGE_GUESTS: 'page-guests',
  PAGE_ROOMS: 'page-rooms',
  PAGE_ROOM_TYPES: 'page-room-types',
  PAGE_SERVICES: 'page-services',
  PAGE_SERVICE_ORDERS: 'page-service-orders',
  PAGE_RESTAURANTS: 'page-restaurants',
  PAGE_RESTAURANT_MENU: 'page-restaurant-menu',
  PAGE_MENU_CATEGORIES: 'page-menu-categories',
  PAGE_RESTAURANT_TABLES: 'page-restaurant-tables',
  PAGE_PHONE_SETTINGS: 'page-phone-settings',
  PAGE_ORDER_HISTORY: 'page-order-history',
  PAGE_CREDIT_ACCOUNTS: 'page-credit-accounts',
  PAGE_SUPPORT_TICKETS: 'page-support-tickets',
  PAGE_USERS: 'page-users',
  PAGE_ID_TYPES: 'page-id-types',
  PAGE_BUDGET: 'page-budget',
  PAGE_BUDGET_SETUP: 'page-budget-setup',
  PAGE_BUDGET_ALLOCATIONS: 'page-budget-allocations',
  PAGE_SETTINGS: 'page-settings',
  PAGE_LOCK_SETTINGS: 'page-lock-settings',
  PAGE_FINANCE_SETUP: 'page-finance-setup',
  PAGE_COMPANIES: 'page-companies',
  PAGE_HOTELS: 'page-hotels',
  PAGE_COMPANY_USERS: 'page-company-users',
  PAGE_SUPER_ADMIN_USERS: 'page-super-admin-users',
  PAGE_ROLES: 'page-roles',
  PAGE_THEME_SETTINGS: 'page-theme-settings',
  PAGE_PAYMENT_SETTINGS: 'page-payment-settings',
  PAGE_COMMUNICATION_SETTINGS: 'page-communication-settings',
  PAGE_POS_SETTINGS: 'page-pos-settings',
  PAGE_KDS_SETTINGS: 'page-kds-settings',
  PAGE_FINANCIAL_REPORTS: 'page-financial-reports',
  PAGE_FINANCE_DASHBOARD: 'page-finance-dashboard',
  PAGE_PENDING_POSTINGS: 'page-pending-postings',
  PAGE_LEDGERS: 'page-ledgers',
  PAGE_JOURNAL_ENTRIES: 'page-journal-entries',
  PAGE_GST_REPORTS: 'page-gst-reports',
  PAGE_REPORTS: 'page-reports',
  // Assets
  PAGE_ASSETS: 'page-assets',
  // Purchase
  PAGE_VENDORS: 'page-vendors',
  PAGE_REQUISITIONS: 'page-requisitions',
  PAGE_PURCHASE_ORDERS: 'page-purchase-orders',
  PAGE_GRN: 'page-grn',
  PAGE_PURCHASE_INVOICES: 'page-purchase-invoices',
  // Inventory
  PAGE_STORES: 'page-stores',
  PAGE_ITEM_MASTER: 'page-item-master',
  PAGE_STOCK_MOVEMENTS: 'page-stock-movements',
  // Online Portal
  PAGE_PORTAL_SETTINGS: 'page-portal-settings',
  PAGE_PROPERTY_REGISTRATIONS: 'page-property-registrations',
  PAGE_PROPERTY_LISTING: 'page-property-listing',
  PAGE_PORTAL_USERS: 'page-portal-users',
  PAGE_PROPERTY_CONFIGURE: 'page-property-configure',
  // Super Admin
  PAGE_CREATE_HOTEL: 'page-create-hotel',
  PAGE_HOTEL_DETAILS: 'page-hotel-details',
  PAGE_PBX_MANAGEMENT: 'page-pbx-management',
  PAGE_AUDIT_TRAIL: 'page-audit-trail',
  PAGE_LIVE_SESSIONS: 'page-live-sessions',
  PAGE_CLOUD_STORAGE: 'page-cloud-storage',
};

export function TabsProvider({ children }) {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  // Store tab-specific state to preserve when switching tabs
  const tabStateRef = useRef({});

  const openTab = useCallback((tabConfig) => {
    const { type, id, title, data } = tabConfig;
    const tabId = `${type}-${id || Date.now()}`;
    
    // Check if tab already exists
    const existingTab = tabs.find(t => t.id === tabId);
    if (existingTab) {
      setActiveTabId(tabId);
      return tabId;
    }

    const newTab = {
      id: tabId,
      type,
      title,
      data,
      createdAt: Date.now()
    };

    setTabs(prev => [...prev, newTab]);
    setActiveTabId(tabId);
    return tabId;
  }, [tabs]);

  const closeTab = useCallback((tabId) => {
    // Clean up tab state when closing
    if (tabStateRef.current[tabId]) {
      delete tabStateRef.current[tabId];
    }
    
    setTabs(prev => {
      const newTabs = prev.filter(t => t.id !== tabId);
      
      // If closing active tab, switch to another tab
      if (activeTabId === tabId && newTabs.length > 0) {
        const closedIndex = prev.findIndex(t => t.id === tabId);
        const newActiveIndex = Math.min(closedIndex, newTabs.length - 1);
        setActiveTabId(newTabs[newActiveIndex]?.id || null);
      } else if (newTabs.length === 0) {
        setActiveTabId(null);
      }
      
      return newTabs;
    });
  }, [activeTabId]);

  const closeAllTabs = useCallback(() => {
    tabStateRef.current = {};
    setTabs([]);
    setActiveTabId(null);
  }, []);

  // Set global reference for logout cleanup
  useEffect(() => {
    globalCloseAllTabs = closeAllTabs;
    return () => {
      globalCloseAllTabs = null;
    };
  }, [closeAllTabs]);

  const updateTabData = useCallback((tabId, newData) => {
    setTabs(prev => prev.map(t => 
      t.id === tabId ? { ...t, data: { ...t.data, ...newData } } : t
    ));
  }, []);

  // Save tab-specific state (for form data, scroll position, etc.)
  const saveTabState = useCallback((tabId, state) => {
    tabStateRef.current[tabId] = { ...tabStateRef.current[tabId], ...state };
  }, []);

  // Get saved tab state
  const getTabState = useCallback((tabId) => {
    return tabStateRef.current[tabId] || null;
  }, []);

  const getActiveTab = useCallback(() => {
    return tabs.find(t => t.id === activeTabId) || null;
  }, [tabs, activeTabId]);

  return (
    <TabsContext.Provider value={{
      tabs,
      activeTabId,
      openTab,
      closeTab,
      closeAllTabs,
      setActiveTabId,
      updateTabData,
      saveTabState,
      getTabState,
      getActiveTab,
      hasTabs: tabs.length > 0
    }}>
      {children}
    </TabsContext.Provider>
  );
}

export function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabs must be used within a TabsProvider');
  }
  return context;
}

export default TabsContext;
